// Punta Tech — small shared building blocks used across sections.
import { C, fonts } from "../theme.js";

// Renders as a link when `href` is given (real navigation, e.g. "#contact"), otherwise
// as a submit/button. Same clipped-corner visual either way.
export const ClippedBtn = ({ children, filled, big, accent, onClick, href, type = "button", disabled, style }) => {
  const a = accent || C.fg;
  const padY = big ? 16 : 13;
  const padX = big ? 28 : 22;
  const Tag = href ? "a" : "button";
  const tagProps = href
    ? { href }
    : { type, onClick, disabled };
  return (
    <Tag
      {...tagProps}
      style={{
        position: "relative",
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: disabled ? "default" : "pointer",
        fontFamily: fonts.mono,
        fontSize: big ? 13 : 11,
        fontWeight: 600,
        letterSpacing: "0.14em",
        color: filled ? "#0a0a0a" : a,
        opacity: disabled ? 0.6 : 1,
        textDecoration: "none",
        ...style,
      }}
    >
      <span
        className={filled ? "btn-filled" : "btn-ghost"}
        style={{
          display: "inline-block",
          padding: `${padY}px ${padX}px`,
          background: filled ? a : "transparent",
          border: `1px solid ${filled ? a : "rgba(255,255,255,0.18)"}`,
          clipPath:
            "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))",
          transition: "background 200ms ease, color 200ms ease, border-color 200ms ease",
        }}
      >
        {children}
      </span>
    </Tag>
  );
};

export const Pill = ({ accent, children }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "7px 14px",
      borderRadius: 999,
      border: `1px solid ${C.borderStrong}`,
      fontFamily: fonts.mono,
      fontSize: 11,
      letterSpacing: "0.16em",
      color: C.fg,
      background: "rgba(255,255,255,0.02)",
      backdropFilter: "blur(6px)",
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: accent,
        boxShadow: `0 0 10px ${accent}`,
      }}
    />
    {children}
  </div>
);

export const ArrowMark = ({ size = 22, accent }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" style={{ display: "block" }}>
    <path d="M2 4 L20 11 L2 18 L7 11 Z" fill="none" stroke={accent} strokeWidth="1.4" strokeLinejoin="miter" />
    <path d="M7 11 L20 11" stroke={accent} strokeWidth="1.4" />
  </svg>
);

export const Hex = ({ size = 22, accent }) => (
  <svg width={size} height={size} viewBox="0 0 28 28">
    <polygon points="14,2 25,8 25,20 14,26 3,20 3,8" fill="none" stroke={accent} strokeWidth="1.3" />
  </svg>
);

export const Divider = ({ accent }) => (
  <div style={{ display: "flex", justifyContent: "center", padding: "0 24px" }}>
    <div style={{ width: "100%", maxWidth: 1280, height: 1, background: accent, opacity: 0.18 }} />
  </div>
);

export const SectionLabel = ({ children, accent }) => (
  <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: "0.22em", color: accent }}>
    {children}
  </div>
);

export const SocialIcon = ({ kind }) => {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (kind === "linkedin") {
    return (
      <svg {...common}>
        <rect x="2" y="2" width="20" height="20" rx="2" />
        <path d="M7 10v7M7 7v.01M11 17v-4a2 2 0 0 1 4 0v4M15 13a2 2 0 0 1 4 0v4" />
      </svg>
    );
  }
  if (kind === "github") {
    return (
      <svg {...common}>
        <path d="M9 19c-4 1.5-4-2-6-2m12 4v-3.5a3 3 0 0 0-.9-2.4c3-.3 6-1.5 6-6.5a4.7 4.7 0 0 0-1.3-3.3 4.4 4.4 0 0 0-.1-3.3s-1-.3-3.4 1.3a11.7 11.7 0 0 0-6 0C7 1.7 6 2 6 2a4.4 4.4 0 0 0-.1 3.3A4.7 4.7 0 0 0 4.5 8.5c0 4.9 3 6.1 6 6.5a3 3 0 0 0-.9 2.3V21" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  );
};

// Controlled field used by LeadForm. `id` drives the label's htmlFor for a11y.
export const Field = ({ id, label, name, type = "text", textarea, accent, value, onChange, required }) => {
  const commonStyle = {
    background: C.bg,
    color: C.fg,
    border: `1px solid ${C.border}`,
    padding: "12px 14px",
    borderRadius: 6,
    fontFamily: fonts.sans,
    fontSize: 14,
    outline: "none",
  };
  const focusHandlers = {
    onFocus: (e) => (e.target.style.borderColor = accent),
    onBlur: (e) => (e.target.style.borderColor = C.border),
  };
  return (
    <label htmlFor={id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.2em", color: C.faint }}>
        {label.toUpperCase()}
        {required ? " *" : ""}
      </span>
      {textarea ? (
        <textarea
          id={id}
          name={name}
          rows={4}
          required={required}
          value={value}
          onChange={onChange}
          style={{ ...commonStyle, resize: "vertical" }}
          {...focusHandlers}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          style={commonStyle}
          {...focusHandlers}
        />
      )}
    </label>
  );
};
